import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { inviteUser } from "../api/mutations";
import { useLanguage } from "../contexts/LanguageContext";
import Drawer from "../blocks/drawer";

export default function InviteUser({ isOpen, onClose, storeId }) {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    email: "",
    role: "member",
    scannerOnly: false,
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const queryClient = useQueryClient();

  const inviteUserMutation = useMutation({
    mutationFn: inviteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["store-users", storeId] });
      handleClose();
    },
  });

  const validate = {
    email: (value) => {
      if (!value.trim()) return t("validation.emailRequired");
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
        return t("validation.invalidEmail");
      return "";
    },
    role: (value) => {
      if (!["admin", "member"].includes(value))
        return t("validation.invalidRole");
      return "";
    },
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (touched[name]) {
      setErrors((prev) => ({ ...prev, [name]: validate[name]?.(value) || "" }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({
      ...prev,
      [name]: validate[name]?.(formData[name]) || "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields
    const newErrors = {};
    Object.keys(validate).forEach((field) => {
      newErrors[field] = validate[field](formData[field]);
    });

    setErrors(newErrors);
    setTouched({ email: true, role: true });

    if (Object.values(newErrors).some((error) => error)) {
      return;
    }

    inviteUserMutation.mutate({
      storeId,
      userData: {
        email: formData.email,
        role: formData.role,
        // Only a member can be restricted; the API ignores it for an admin.
        dashboard_access: !(formData.role === "member" && formData.scannerOnly),
      },
    });
  };

  const handleClose = () => {
    setFormData({ email: "", role: "member", scannerOnly: false });
    setErrors({});
    setTouched({});
    onClose();
  };

  return (
    <Drawer
      open={isOpen}
      setOpen={onClose}
      header={t("team.inviteUser")}
      isForm={true}
      onSubmit={handleSubmit}
      onCancel={() => {
        onClose();
      }}
      submitText={t("common.save")}
      cancelText={t("common.cancel")}
      isLoading={inviteUserMutation.isPending}
      submitDisabled={inviteUserMutation.isPending}
    >
      <div className="px-4 py-6 sm:px-6 space-y-4">
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700"
          >
            {t("common.email")}
          </label>
          <input
            type="email"
            name="email"
            id="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`mt-1 block w-full rounded-md shadow-xs sm:text-sm ${
              errors.email ? "border-red-300" : "border-gray-300"
            }`}
          />
          {errors.email && touched.email && (
            <p className="mt-2 text-sm text-red-600">{errors.email}</p>
          )}
        </div>

        {/* Role Select */}
        <div>
          <label
            htmlFor="role"
            className="block text-sm font-medium text-gray-700"
          >
            {t("common.role")}
          </label>
          <select
            name="role"
            id="role"
            value={formData.role}
            onChange={handleChange}
            onBlur={handleBlur}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-xs sm:text-sm"
          >
            <option value="member">{t("roles.member")}</option>
            <option value="admin">{t("roles.admin")}</option>
          </select>
        </div>

        {formData.role === "member" && (
          <div className="relative flex items-start">
            <div className="flex h-6 items-center">
              <input
                id="scannerOnly"
                name="scannerOnly"
                type="checkbox"
                checked={formData.scannerOnly}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    scannerOnly: e.target.checked,
                  }))
                }
                className="h-4 w-4 rounded-sm border-gray-300 text-indigo-600 focus:ring-indigo-600"
              />
            </div>
            <div className="ml-3 text-sm leading-6">
              <label htmlFor="scannerOnly" className="font-medium text-gray-900">
                {t("team.scannerOnly.inviteLabel")}
              </label>
              <p className="text-gray-500">{t("team.scannerOnly.inviteHelp")}</p>
            </div>
          </div>
        )}

        {inviteUserMutation.isError && (
          <div className="mt-2 text-sm text-red-600">
            {t("errors.inviteError")}
          </div>
        )}
      </div>
    </Drawer>
  );
}
